import type { Account } from '@interfaces/account';
import type { TranslatedString } from '@interfaces/shared';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import type { AccountGroupType } from '@ovComponents/4-module/widgets/editButton/pageTypes/components/editAccountGroupDialog';
import type { DropResult } from 'react-beautiful-dnd';

export const moveAccount = (
  fromArr: Account[],
  toArr: Account[],
  fromIndex: number,
  toIndex?: number,
) => {
  const fromList = [...fromArr];
  const [moved] = fromList.splice(fromIndex, 1);

  const toList = [...toArr];
  if (toIndex !== undefined) {
    toList.splice(toIndex, 0, moved);
  } else {
    toList.push(moved);
  }

  return { fromList, toList, moved };
};

export const handleAccountGroupDragEnd =
  ({
    accountGroup,
    setAccountGroup,
    ungroupedAccounts,
    setUngroupedAccounts,
    updateAccountGroup,
    setAppendAccounts,
    setRemoveAccounts,
    appendAccounts,
    removeAccounts,
    variant,
    disableAccountsReordering = false,
  }: {
    accountGroup: AccountGroupType;
    setAccountGroup: (accountGroup: AccountGroupType) => void;
    ungroupedAccounts: Account[];
    setUngroupedAccounts: (accounts: Account[]) => void;
    updateAccountGroup: (
      key: string,
      value: TranslatedString | Account[],
    ) => void;
    setAppendAccounts?: (accounts: Account[]) => void;
    setRemoveAccounts?: (accounts: Account[]) => void;
    appendAccounts?: Account[];
    removeAccounts?: Account[];
    variant: 'create' | 'edit';
    disableAccountsReordering?: boolean;
  }) =>
  ({ source, destination }: DropResult) => {
    if (!destination) return;

    const isAccountGroupDroppableId = (id: string) =>
      variant === 'create'
        ? id.startsWith('accountGroup-')
        : accountGroup.id === id;

    // Reorder accounts within same account group
    if (
      isAccountGroupDroppableId(source.droppableId) &&
      source.droppableId === destination.droppableId &&
      !disableAccountsReordering
    ) {
      if (source.index === destination.index) return;
      const accounts = Array.from(accountGroup.accounts);
      const [removed] = accounts.splice(source.index, 1);
      accounts.splice(destination.index, 0, removed);
      updateAccountGroup('accounts', accounts);
    }

    const ungroupedAccountSourceIndex = ungroupedAccounts?.[source.index];

    // Create-workflow: creating first account group from placeholder
    if (
      variant === 'create' &&
      accountGroup.accounts.length === 0 &&
      source.droppableId === 'ungroupedAccounts' &&
      destination.droppableId === 'placeholder'
    ) {
      const sourceIdxInUngrouped = ungroupedAccountSourceIndex
        ? ungroupedAccounts.findIndex(
            (acc) => acc.id === ungroupedAccountSourceIndex.id,
          )
        : source.index;
      const { fromList, moved } = moveAccount(
        ungroupedAccounts,
        [],
        sourceIdxInUngrouped,
      );
      setUngroupedAccounts(fromList);
      setAccountGroup({
        id: `accountGroup-${Date.now()}`,
        translatedName: getLanguageDefaults(),
        accounts: [moved],
      });
      return;
    }

    // Move account from ungrouped section to account group
    if (
      source.droppableId === 'ungroupedAccounts' &&
      isAccountGroupDroppableId(destination.droppableId)
    ) {
      const sourceIdxInUngrouped = ungroupedAccountSourceIndex
        ? ungroupedAccounts.findIndex(
            (acc) => acc.id === ungroupedAccountSourceIndex.id,
          )
        : source.index;
      const targetAccounts = accountGroup.accounts || [];
      const { fromList, toList, moved } = moveAccount(
        ungroupedAccounts,
        targetAccounts,
        sourceIdxInUngrouped,
        destination.index,
      );
      setUngroupedAccounts(fromList);
      updateAccountGroup('accounts', toList);

      const accountExists = removeAccounts?.find((acc) => acc.id === moved.id);
      if (accountExists) {
        setRemoveAccounts?.(
          removeAccounts?.filter((acc) => acc.id !== moved.id) || [],
        );
      } else {
        setAppendAccounts?.([...(appendAccounts || []), moved]);
      }

      return;
    }

    // Move account from account group to ungrouped section
    if (
      isAccountGroupDroppableId(source.droppableId) &&
      destination.droppableId === 'ungroupedAccounts'
    ) {
      const { fromList, toList, moved } = moveAccount(
        accountGroup.accounts,
        ungroupedAccounts,
        source.index,
        destination.index,
      );
      updateAccountGroup('accounts', fromList);
      setUngroupedAccounts(toList);

      const accountExists = appendAccounts?.find((acc) => acc.id === moved.id);
      if (accountExists) {
        setAppendAccounts?.(
          appendAccounts?.filter((acc) => acc.id !== moved.id) || [],
        );
      } else {
        setRemoveAccounts?.([...(removeAccounts || []), moved]);
      }

      return;
    }
  };
